import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * Header navigation bar.
 *
 * State attributes (all consumed by the section's CSS):
 * - data-scrolled  — page is scrolled past the top (IntersectionObserver sentinel)
 * - data-open      — a desktop dropdown panel is open (drives underlay + color flip)
 * - data-transparent — rendered by Liquid on the homepage; JS never touches it
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [panels] - Desktop dropdown panels.
 *
 * @extends {Component<Refs>}
 */
class HeaderNavComponent extends Component {
  #closeTimer = null;
  #sentinel = null;
  #observer = null;
  #trackingPointer = false;
  /** @type {{x: number, y: number} | null} Last known pointer position. Tracked only while a panel is open. */
  #pointer = null;
  /** @type {string | null} Panel held open by a theme editor block selection. */
  #pinnedPanelId = null;

  connectedCallback() {
    super.connectedCallback();

    // The sentinel must live inside the scroll container (.page-wrapper is
    // the scroller on desktop, body on mobile) so it scrolls with content.
    const scroller = this.closest('.page-wrapper') ?? document.body;
    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    this.#sentinel.style.cssText = 'position:static;height:1px;margin-bottom:-1px;pointer-events:none;';
    scroller.prepend(this.#sentinel);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-scrolled', !entry.isIntersecting);
    });
    this.#observer.observe(this.#sentinel);

    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('focusin', this.#onFocusIn);

    // Theme editor only: selecting a panel (or a block inside one) in the
    // sidebar opens that panel and pins it open until the block is deselected.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#sentinel?.remove();
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('focusin', this.#onFocusIn);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.cancelClose();
    this.#stopPointerTracking();
  }

  get #panels() {
    return this.refs.panels ?? [];
  }

  get #activePanelId() {
    return this.#panels.find((panel) => panel.hasAttribute('data-active'))?.dataset.panelId ?? null;
  }

  /** Opens the panel for the hovered nav item. Bound via on:pointerenter on the <li>. */
  openPanel(event) {
    const id = event.target.dataset.panel;
    if (!id) return;

    this.#trackPointer(event);
    this.#pinnedPanelId = null;
    this.cancelClose();
    this.#setActive(id);
  }

  /** Toggles a panel from its caret button (keyboard/click path). */
  togglePanel(event) {
    const button = event.target.closest('[data-panel-toggle]');
    const id = button?.dataset.panelToggle;
    if (!id) return;

    this.#pinnedPanelId = null;
    this.cancelClose();
    this.#setActive(this.#activePanelId === id ? null : id);
  }

  /** Schedules closing after a short delay so the pointer can travel bar → panel. */
  scheduleClose() {
    this.cancelClose();
    if (this.#pinnedPanelId) return;
    this.#closeTimer = setTimeout(this.#closeIfPointerAway, 200);
  }

  /**
   * Closes only if the pointer really has left the open menu.
   *
   * `pointerenter` on the panel is what normally cancels the close, but it never
   * fires if something is layered on top of the panel — the theme editor draws
   * its block outline and label over the previewed block, so the handoff from the
   * nav item to the panel is swallowed and the panel closes out from under the
   * cursor. Hit-testing the last pointer position against the panel's own box is
   * immune to whatever is painted over it.
   */
  #closeIfPointerAway = () => {
    this.#closeTimer = null;

    if (this.#pinnedPanelId) return;

    if (this.#isPointerOverMenu()) {
      this.#closeTimer = setTimeout(this.#closeIfPointerAway, 200);
      return;
    }

    this.#setActive(null);
  };

  #isPointerOverMenu() {
    if (!this.#pointer) return false;

    const { x, y } = this.#pointer;
    const targets = [
      ...this.#panels.filter((panel) => panel.hasAttribute('data-active')),
      ...this.querySelectorAll('[data-panel][data-active]'),
    ];

    return targets.some((element) => {
      const rect = element.getBoundingClientRect();
      return x >= rect.left && x < rect.right && y >= rect.top && y < rect.bottom;
    });
  }

  cancelClose() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  /** Closes all panels. Also bound to the underlay click. */
  closeAllPanels() {
    this.#pinnedPanelId = null;
    this.cancelClose();
    this.#setActive(null);
  }

  #setActive(id) {
    for (const panel of this.#panels) {
      panel.toggleAttribute('data-active', panel.dataset.panelId === id);
    }

    for (const panelToggle of this.querySelectorAll('[data-panel]')) {
      panelToggle.toggleAttribute('data-active', panelToggle.dataset.panel === id);
      panelToggle.setAttribute('aria-expanded', panelToggle.dataset.panel === id);
    }

    this.toggleAttribute('data-open', Boolean(id));

    if (id) {
      this.#startPointerTracking();
    } else {
      this.#stopPointerTracking();
    }
  }

  #startPointerTracking() {
    if (this.#trackingPointer) return;
    this.#trackingPointer = true;
    document.addEventListener('pointermove', this.#trackPointer, { passive: true });
    document.documentElement.addEventListener('pointerleave', this.#onPointerLeaveWindow);
  }

  #stopPointerTracking() {
    this.#trackingPointer = false;
    this.#pointer = null;
    document.removeEventListener('pointermove', this.#trackPointer);
    document.documentElement.removeEventListener('pointerleave', this.#onPointerLeaveWindow);
  }

  #trackPointer = (event) => {
    this.#pointer = { x: event.clientX, y: event.clientY };
  };

  /** The pointer left the document, so there is no position left to hit-test. */
  #onPointerLeaveWindow = () => {
    this.#pointer = null;
    this.scheduleClose();
  };

  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const panel = target.closest('[data-panel-id]');
    if (!panel) return;

    this.cancelClose();
    this.#pinnedPanelId = panel.dataset.panelId;
    this.#setActive(this.#pinnedPanelId);
  };

  #onBlockDeselect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;
    if (!target.closest('[data-panel-id]')) return;

    this.#pinnedPanelId = null;
    this.scheduleClose();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('data-open')) {
      this.closeAllPanels();
    }
  };

  /** Closes panels when keyboard focus leaves the header entirely. */
  #onFocusIn = (event) => {
    if (this.hasAttribute('data-open') && !this.contains(event.target)) {
      this.closeAllPanels();
    }

    const parentPanel = event.target.closest('[data-panel]');
    if (!parentPanel) return;

    this.#setActive(parentPanel.dataset.panel);
  };
}

/**
 * Full-screen mobile navigation drawer.
 *
 * Extends the shared DialogComponent (focus trap, scroll lock, Esc,
 * click-outside, dialog-closing animation convention). Adds the
 * main → sub-panel slide navigation.
 *
 * @extends {DialogComponent}
 */
class NavDrawerComponent extends DialogComponent {
  constructor() {
    super();
    this.focusElement = this.querySelector('dialog');

    // closeDialog is an instance class field on DialogComponent, so it can't
    // be overridden with a super-call. Reset via the dialog:close event the
    // base class dispatches instead.
    this.addEventListener(DialogOpenEvent.eventName, () => {
      this.#reflectOpenState(true);
      document.addEventListener('click', this.#onDocumentClick);
    });
    this.addEventListener(DialogCloseEvent.eventName, () => {
      document.removeEventListener('click', this.#onDocumentClick);
      this.#resetSubpanels();
      this.#reflectOpenState(false);
    });
  }

  /**
   * Closes the drawer on any click outside it.
   *
   * The base class's own click-outside handler is bound to the component, so it
   * only ever sees clicks that land inside the dialog. While the drawer was modal
   * that was enough — the full-viewport ::backdrop retargeted every outside click
   * to the <dialog> itself. Non-modal has no backdrop, so the listener has to sit
   * on the document to catch clicks on the underlay, the header and the page.
   *
   * Safe to add during DialogOpenEvent: showDialog() dispatches that inside a
   * requestAnimationFrame, so the click that opened the drawer has finished
   * dispatching by the time this is registered.
   */
  #onDocumentClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    const target = event.target;
    if (!(target instanceof Element) || dialog.contains(target)) return;

    // The hamburger is the drawer's own toggle. Leave it to toggleDialog so both
    // handlers don't run a close on the same click.
    if (target.closest('.header__hamburger')) return;

    this.closeDialog();
  };

  /**
   * Mirrors the drawer's open state onto the header (drives the solid
   * background + hamburger/close icon swap) and the trigger button's ARIA.
   * The drawer is non-modal and slides out under the header bar, so the
   * hamburger stays interactive and acts as the close toggle.
   */
  #reflectOpenState(open) {
    const header = this.closest('header-nav-component');
    header?.toggleAttribute('data-drawer-open', open);

    const trigger = header?.querySelector('.header__hamburger');
    trigger?.setAttribute('aria-expanded', String(open));
    trigger?.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  }

  /** Opens a sub-panel by handle. Bound via on:click="/openSubpanel/<handle>". */
  openSubpanel(handle) {
    for (const panel of this.querySelectorAll('[data-subpanel]')) {
      panel.toggleAttribute('data-active', panel.dataset.subpanel === handle);
    }

    for (const trigger of this.querySelectorAll('[data-subpanel-trigger]')) {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.subpanelTrigger === handle));
    }

    this.toggleAttribute('data-subpanel-open', true);
    // this.querySelector(`[data-subpanel='${handle}'] .header-drawer__back`)?.focus();
  }

  closeSubpanel() {
    const active = this.querySelector('[data-subpanel][data-active]');
    const trigger = active ? this.querySelector(`[data-subpanel-trigger='${active.dataset.subpanel}']`) : null;

    this.#resetSubpanels();
    // trigger?.focus();
  }

  #resetSubpanels() {
    for (const panel of this.querySelectorAll('[data-subpanel]')) {
      panel.removeAttribute('data-active');
    }

    for (const trigger of this.querySelectorAll('[data-subpanel-trigger]')) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.removeAttribute('data-subpanel-open');
  }
}

if (!customElements.get('header-nav-component')) {
  customElements.define('header-nav-component', HeaderNavComponent);
}

if (!customElements.get('nav-drawer-component')) {
  customElements.define('nav-drawer-component', NavDrawerComponent);
}
